/**
 * BookingCancelDialog.tsx
 * Dialog zum Stornieren einer Buchung mit Rückerstattungs-Optionen
 */

import React, { useState } from 'react';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { CancellationForm } from '../../types/booking.types';
import './BookingCancelDialog.css';

interface BookingForCancel {
  id: string;
  bookingNumber: string;
  customerName: string;
  tripDate: string;
  tripTime: string;
  participantCount: number;
  totalAmount: number;
  paidAmount: number;
  // Allow additional properties from the full Booking type
  [key: string]: any;
}

interface BookingCancelDialogProps {
  booking: BookingForCancel | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (cancellation: CancellationForm) => Promise<void>;
}

const BookingCancelDialog: React.FC<BookingCancelDialogProps> = ({
  booking,
  isOpen,
  onClose,
  onConfirm
}) => {
  // State
  const [reason, setReason] = useState('');
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundMethod, setRefundMethod] = useState<'bank' | 'voucher' | 'none'>('bank');
  const [sendConfirmation, setSendConfirmation] = useState(true);
  const [internalNote, setInternalNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  React.useEffect(() => {
    if (isOpen && booking) {
      setReason('');
      setRefundAmount(booking.paidAmount);
      setRefundMethod('bank');
      setSendConfirmation(true);
      setInternalNote('');
      setConfirmed(false);
      setError(null);
    }
  }, [isOpen, booking]);

  if (!isOpen || !booking) return null;

  // Validierung
  const validateForm = (): boolean => {
    if (reason.trim().length < 10) {
      setError('Grund muss mindestens 10 Zeichen haben');
      return false;
    }
    if (refundAmount < 0 || refundAmount > booking.paidAmount) {
      setError(`Betrag muss zwischen 0 und ${booking.paidAmount.toFixed(2)} EUR liegen`);
      return false;
    }
    if (!confirmed) {
      setError('Bitte Stornierung durch Checkbox bestätigen');
      return false;
    }
    return true;
  };

  // Handle submit
  const handleSubmit = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const cancellationData: CancellationForm = {
        reason,
        refundAmount,
        refundMethod,
        sendConfirmation,
        internalNote
      };

      await onConfirm(cancellationData);
      onClose();
    } catch (err: any) {
      console.error('Error cancelling booking:', err);
      setError(err.message || 'Fehler beim Stornieren');
    } finally {
      setLoading(false);
    }
  };

  // Suggested cancellation reasons
  const suggestedReasons = [
    'Kunde hat storniert - Terminkollision',
    'Kunde hat storniert - Wetterbedenken',
    'Kunde hat storniert - Gruppengröße geändert',
    'Technischer Defekt am Floß',
    'Wetterbedingungen nicht sicher',
    'Doppelbuchung - administrativer Fehler'
  ];

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <div className="cancel-dialog-overlay" onClick={onClose}>
      <div className="cancel-dialog" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cancel-dialog-header">
          <h3 className="cancel-dialog-header-title">
            <ExclamationTriangleIcon />
            Buchung stornieren
          </h3>
          <button
            onClick={onClose}
            className="cancel-dialog-close"
            disabled={loading}
          >
            <XMarkIcon />
          </button>
        </div>

        {/* Body */}
        <div className="cancel-dialog-body">
          {/* Error Display */}
          {error && (
            <div className="cancel-error-box">
              <ExclamationTriangleIcon />
              <span>{error}</span>
            </div>
          )}

          {/* Booking Summary */}
          <div className="cancel-booking-summary">
            <div className="cancel-booking-summary-item">
              <label>Buchungsnr.</label>
              <span>{booking.bookingNumber}</span>
            </div>
            <div className="cancel-booking-summary-item">
              <label>Kunde</label>
              <span>{booking.customerName}</span>
            </div>
            <div className="cancel-booking-summary-item">
              <label>Termin</label>
              <span>{formatDate(booking.tripDate)}, {booking.tripTime}</span>
            </div>
            <div className="cancel-booking-summary-item">
              <label>Teilnehmer</label>
              <span>{booking.participantCount}</span>
            </div>
            <div className="cancel-booking-summary-item">
              <label>Betrag</label>
              <span>{formatCurrency(booking.totalAmount)}</span>
            </div>
          </div>

          {/* Warning */}
          <div className="cancel-warning-box">
            <ExclamationTriangleIcon />
            <p>
              <strong>Achtung:</strong> Diese Aktion kann nicht rückgängig gemacht werden.
              Die Buchung wird dauerhaft als storniert markiert und die Kapazität freigegeben.
            </p>
          </div>

          {/* Reason Section */}
          <div className="cancel-form-group">
            <label className="cancel-form-label">
              Stornierungsgrund <span className="required">*</span>
            </label>
            <select
              onChange={(e) => setReason(e.target.value)}
              className="cancel-form-select"
              disabled={loading}
              style={{ marginBottom: '10px' }}
            >
              <option value="">-- Vorlage auswählen --</option>
              {suggestedReasons.map((r, idx) => (
                <option key={idx} value={r}>{r}</option>
              ))}
            </select>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Grund für die Stornierung (min. 10 Zeichen)..."
              rows={3}
              className="cancel-form-textarea"
              disabled={loading}
            />
            <div className={`cancel-char-count ${reason.length >= 10 ? 'valid' : ''}`}>
              {reason.length} / 10 Zeichen
            </div>
          </div>

          {/* Refund Section */}
          <div className="cancel-refund-section">
            <div className="cancel-refund-title">
              <CurrencyEuroIcon />
              Rückerstattung
            </div>
            <div className="cancel-refund-grid">
              <div>
                <label className="cancel-form-label">Betrag</label>
                <input
                  type="number"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(parseFloat(e.target.value) || 0)}
                  min={0}
                  max={booking.paidAmount}
                  step={0.01}
                  className="cancel-form-input"
                  disabled={loading}
                />
                <div className="cancel-form-hint">
                  Max: {formatCurrency(booking.paidAmount)}
                </div>
              </div>
              <div>
                <label className="cancel-form-label">Methode</label>
                <select
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value as any)}
                  className="cancel-form-select"
                  disabled={loading || refundAmount === 0}
                >
                  <option value="bank">Banküberweisung</option>
                  <option value="voucher">Gutschein</option>
                  <option value="none">Keine</option>
                </select>
              </div>
            </div>
          </div>

          {/* Internal Note */}
          <div className="cancel-form-group">
            <label className="cancel-form-label">
              Interne Notiz (optional)
            </label>
            <textarea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder="Nur für Admins sichtbar..."
              rows={2}
              className="cancel-form-textarea"
              disabled={loading}
            />
          </div>

          {/* Email Confirmation */}
          <div className="cancel-form-group">
            <label className="cancel-checkbox-label">
              <input
                type="checkbox"
                checked={sendConfirmation}
                onChange={(e) => setSendConfirmation(e.target.checked)}
                disabled={loading}
              />
              <EnvelopeIcon />
              <span>Stornierungsbestätigung per E-Mail senden</span>
            </label>
          </div>

          {/* Confirmation Checkbox */}
          <div className="cancel-confirmation-box">
            <label>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={loading}
              />
              <span>
                <strong>Ich bestätige die Stornierung dieser Buchung.</strong>
                Mir ist bewusst, dass diese Aktion nicht rückgängig gemacht werden kann.
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="cancel-dialog-footer">
          <button
            onClick={onClose}
            className="cancel-btn-secondary"
            disabled={loading}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            className="cancel-btn-danger"
            disabled={loading || !confirmed || reason.length < 10}
          >
            {loading ? (
              <>
                <div className="cancel-spinner"></div>
                Storniere...
              </>
            ) : (
              <>
                <CheckCircleIcon />
                Stornieren
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingCancelDialog;
